import { useContext, useMemo } from 'react'

import { MarkdownBody } from '@design-system/components/MarkdownBody'
import { KnowledgeAskCitation, KnowledgeDocumentListItem } from '@digital-twins/shared-types'

import {
  hasStudyDocEmbeds,
  StudyDocBody,
  StudyDocPlotLookup,
  StudyDocPrintChrome,
  StudyDocQuote,
} from '@/components/study-doc'
import { StudyDocDecisionConfidence } from '@/components/study-doc/StudyDocDecisionMasthead/StudyDocDecisionMasthead'
import { QuickStudyCutPlotsContext } from '@/components/quick-study-results/quickStudyCutPlots'
import { StudyDocStripLookup } from '@/components/study-doc/StudyDocMagazineBand/resolveMagazineBandSource'
import { ThreadAssetView } from '@/lib/api'

import { createCreativeMarkdownHandlers } from '../resolveCreativeMarkdown'
import { parseGateQuestions } from './gateArtifacts'
import { RunGateQuestionList } from './RunGateQuestionList'

/**
 * Read-only body for a rail / expanded artifact.
 *
 * Questions payloads (`parseGateQuestions`) render as cards, never as fenced
 * JSON. Everything else stays markdown.
 */

export interface ThreadArtifactBodyPreviewProps {
  body: string
  /**
   * Gate-list position — namespaces `run-gate-question-*` testids.
   * Rail and expanded view both use 0; they never mount two lists at once.
   */
  position?: number
  /** Compact density for the rail; document density for the expanded takeover. */
  density?: 'compact' | 'default'
  plots?: Record<string, StudyDocPlotLookup>
  quotes?: Record<string, StudyDocQuote>
  plotRegion?: 'all' | 'plot'
  /** Slim picker population for mosaic tiles missing a row image. */
  populationId?: string
  /** FactBundle strip lookup for `{{quotes_strip:…}}` full-bleed. */
  strips?: Record<string, StudyDocStripLookup>
  /** P(best) masthead. Absent → empty-stable, no masthead. */
  decisionConfidence?: StudyDocDecisionConfidence | null
  threadAssets?: Array<ThreadAssetView>
  docsByToken?: Map<string, KnowledgeDocumentListItem>
  onOpenCitation?: (index: number, citation: KnowledgeAskCitation) => void
  /** The run the embedded reads belong to: a Compare-segments pick asks it for cut plots (W6-QS-c). */
  runToken?: string
}

export function ThreadArtifactBodyPreview(
  props: ThreadArtifactBodyPreviewProps,
) {
  const {
    body,
    position = 0,
    density = 'default',
    plots,
    quotes,
    plotRegion,
    populationId,
    strips,
    decisionConfidence,
    threadAssets,
    docsByToken,
    onOpenCitation,
    runToken,
  } = props

  const questions = parseGateQuestions(body)
  // A preview of the enclosing surface's run keeps its revision; another run's preview
  // asks by its token alone; a preview with no run keeps whatever the surface provides.
  const enclosingCutPlotsSource = useContext(QuickStudyCutPlotsContext)
  const cutPlotsSource = useMemo(
    () =>
      !runToken || enclosingCutPlotsSource?.runToken === runToken
        ? enclosingCutPlotsSource
        : { runToken },
    [runToken, enclosingCutPlotsSource],
  )
  const creative = useMemo(
    () =>
      createCreativeMarkdownHandlers({
        assets: threadAssets,
        docsByToken,
        onOpenCitation,
      }),
    [threadAssets, docsByToken, onOpenCitation],
  )

  if (questions) {
    return (
      <div
        className="rounded-lg bg-background"
        data-testid="thread-artifact-questions-preview"
      >
        <RunGateQuestionList questions={questions} position={position} />
      </div>
    )
  }

  if (hasStudyDocEmbeds(body) || plots) {
    return (
      <QuickStudyCutPlotsContext.Provider value={cutPlotsSource}>
        <StudyDocBody
            markdown={body}
            layout="lite-magazine"
            plots={plots}
            quotes={quotes}
            plotRegion={plotRegion}
            populationId={populationId}
            strips={strips}
            decisionConfidence={decisionConfidence}
            className={
            density === 'compact' ? 'text-sm text-muted-foreground' : undefined
          }
        />
      </QuickStudyCutPlotsContext.Provider>
    )
  }

  const printChrome = (
    <StudyDocPrintChrome />
  )

  if (density === 'compact') {
    return (
      <>
        {printChrome}
        <MarkdownBody
          content={body}
          density="compact"
          className="text-sm text-muted-foreground"
          urlTransform={creative.urlTransform}
          renderImage={creative.renderImage}
          renderLink={creative.renderLink}
        />
      </>
    )
  }

  return (
    <>
      {printChrome}
      <MarkdownBody
        content={body}
        density="default"
        urlTransform={creative.urlTransform}
        renderImage={creative.renderImage}
        renderLink={creative.renderLink}
      />
    </>
  )
}
